import type { DataProvenance } from './instrumentalAnalysis';

// ---- Gravimetric analysis: precipitate, dry or ignite to constant mass, calculate % mass ----

/** Standard atomic weights, g mol⁻¹ (IUPAC CIAAW abridged values, conventional where a range exists). */
export const ATOMIC_MASSES = {
  H: 1.008,
  C: 12.011,
  N: 14.007,
  O: 15.999,
  Mg: 24.305,
  P: 30.974,
  S: 32.06,
  Cl: 35.45,
  Fe: 55.845,
  Ni: 58.693,
  Ag: 107.87,
  Ba: 137.33,
} as const;

export type AtomSymbol = keyof typeof ATOMIC_MASSES;
export type Composition = Partial<Record<AtomSymbol, number>>;

/** Model criterion: two successive weighings within this mass are taken as constant mass. */
export const CONSTANT_MASS_TOLERANCE_G = 0.0005;
/** Analytical balance resolution used for every displayed mass. */
export const BALANCE_DECIMALS = 4;
/** Relative tolerance for a learner percentage (rounding of molar masses and weighings). */
export const GRAVIMETRIC_RELATIVE_TOLERANCE = 0.01;
/**
 * Extra mass (water or volatile matter) left after each heating, as a fraction of the dry
 * precipitate mass. The last two weighings agree within the constant-mass tolerance.
 */
export const RESIDUAL_WATER_PATTERN: readonly number[] = [0.12, 0.018, 0.0006, 0];
export const MAX_WEIGHINGS = RESIDUAL_WATER_PATTERN.length;
const PERCENT = 100;

export function molarMass(composition: Composition): number {
  return (Object.keys(composition) as AtomSymbol[]).reduce(
    (total, symbol) => total + ATOMIC_MASSES[symbol] * (composition[symbol] ?? 0),
    0,
  );
}

export function roundMass(value: number): number {
  const factor = 10 ** BALANCE_DECIMALS;
  return Math.round(value * factor) / factor;
}

export type ErrorEffect = 'high' | 'low';

export interface GravimetricErrorSource {
  id: string;
  label: string;
  effect: ErrorEffect;
  explanation: string;
}

export interface GravimetricProblem extends DataProvenance {
  id: string;
  title: string;
  context: string;
  /** Species the percentage is reported for, e.g. Cl or SO₄²⁻. */
  analyte: string;
  analyteName: string;
  analyteComposition: Composition;
  /** Form that is finally weighed, e.g. AgCl or Fe₂O₃. */
  weighedForm: string;
  weighedFormName: string;
  weighedComposition: Composition;
  /** Analyte units per formula unit of the weighed form. */
  analytePerWeighedForm: number;
  ratioExplanation: string;
  equations: string[];
  /** How the precipitate is brought to constant mass. */
  treatment: string;
  sampleMassG: number;
  emptyCrucibleG: number;
  /** Teaching composition of the sample, % by mass of the analyte. */
  modelPercent: number;
  errorSources: GravimetricErrorSource[];
}

const GRAVIMETRIC_SOURCES = [
  'IUPAC CIAAW standard atomic weights (abridged values)',
  'Standard quantitative analysis texts (Vogel’s Textbook of Quantitative Chemical Analysis; Skoog et al., Fundamentals of Analytical Chemistry): precipitants, weighing forms and drying or ignition practice',
];
const GRAVIMETRIC_CONDITIONS =
  'Masses in grams on an analytical balance read to 0.0001 g. Constant mass in this model means two successive weighings agree within 0.0005 g. Sample compositions are teaching values, not measurements of real products.';

const provenance = {
  sources: GRAVIMETRIC_SOURCES,
  conditions: GRAVIMETRIC_CONDITIONS,
  reviewStatus: 'pending-teaching-review',
} as const;

const DRY_IN_OVEN = 'Dry in an oven at about 110 °C, cool in a desiccator and weigh. Repeat until the mass is constant.';
const IGNITE = 'Ignite (heat strongly) in a furnace, cool in a desiccator and weigh. Repeat until the mass is constant.';

const NOT_DRIED: GravimetricErrorSource = {
  id: 'not-dried',
  label: 'Weighed before constant mass was reached',
  effect: 'high',
  explanation: 'Water still in the precipitate is weighed as if it were precipitate, so the calculated analyte mass is too large.',
};
const INCOMPLETE_PRECIPITATION: GravimetricErrorSource = {
  id: 'incomplete',
  label: 'Too little precipitating reagent added (incomplete precipitation)',
  effect: 'low',
  explanation: 'Some analyte stays dissolved in the filtrate and is never weighed. Add excess reagent and test the clear filtrate with one more drop.',
};
const COPRECIPITATION: GravimetricErrorSource = {
  id: 'coprecipitation',
  label: 'Other ions trapped in the precipitate (co-precipitation)',
  effect: 'high',
  explanation: 'Trapped or surface-adsorbed ions add mass that is counted as analyte. Precipitate slowly from hot, dilute solution and wash well.',
};
const WASHING_LOSS: GravimetricErrorSource = {
  id: 'washing-loss',
  label: 'Precipitate lost through the filter or left in the beaker during washing',
  effect: 'low',
  explanation: 'Every particle that is lost is analyte that is not weighed. Use a fine filter, rinse the beaker into the filter and wash with small portions.',
};

export const GRAVIMETRIC_PROBLEMS: GravimetricProblem[] = [
  {
    ...provenance,
    id: 'chloride-pool-salt',
    title: 'Chloride in pool salt',
    context: 'A 0.2500 g sample of pool salt is dissolved in water and acidified with dilute nitric acid. Excess silver nitrate solution precipitates all of the chloride.',
    analyte: 'Cl', analyteName: 'chloride (as Cl)',
    analyteComposition: { Cl: 1 },
    weighedForm: 'AgCl', weighedFormName: 'silver chloride',
    weighedComposition: { Ag: 1, Cl: 1 },
    analytePerWeighedForm: 1,
    ratioExplanation: 'Each AgCl contains one Cl, so n(Cl) = n(AgCl).',
    equations: ['Ag⁺(aq) + Cl⁻(aq) → AgCl(s)'],
    treatment: DRY_IN_OVEN,
    sampleMassG: 0.25, emptyCrucibleG: 18.4372, modelPercent: 59.8,
    errorSources: [
      INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED,
      {
        id: 'light',
        label: 'Silver chloride left in bright light',
        effect: 'low',
        explanation: 'Light decomposes AgCl to silver and chlorine gas. Chlorine escapes, so the weighed mass is lower than it should be.',
      },
    ],
  },
  {
    ...provenance,
    id: 'sulfate-lawn-fertiliser',
    title: 'Sulfate in lawn fertiliser',
    context: 'A 0.4000 g sample of an ammonium sulfate lawn fertiliser is dissolved and acidified with dilute hydrochloric acid. Excess barium chloride precipitates the sulfate.',
    analyte: 'SO₄²⁻', analyteName: 'sulfate',
    analyteComposition: { S: 1, O: 4 },
    weighedForm: 'BaSO₄', weighedFormName: 'barium sulfate',
    weighedComposition: { Ba: 1, S: 1, O: 4 },
    analytePerWeighedForm: 1,
    ratioExplanation: 'Each BaSO₄ contains one SO₄²⁻ ion, so n(SO₄²⁻) = n(BaSO₄).',
    equations: ['Ba²⁺(aq) + SO₄²⁻(aq) → BaSO₄(s)'],
    treatment: IGNITE,
    sampleMassG: 0.4, emptyCrucibleG: 21.0856, modelPercent: 70.5,
    errorSources: [
      INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED,
      {
        id: 'reduction',
        label: 'Filter paper charred without enough air during ignition',
        effect: 'low',
        explanation: 'Carbon from the paper can reduce some BaSO₄ to BaS, which has a smaller mass.',
      },
    ],
  },
  {
    ...provenance,
    id: 'sulfur-garden-fertiliser',
    title: 'Sulfur in a garden fertiliser',
    context: 'All the sulfur in a 0.5000 g fertiliser sample is first oxidised to sulfate. Excess barium chloride then precipitates it as barium sulfate.',
    analyte: 'S', analyteName: 'sulfur (as S)',
    analyteComposition: { S: 1 },
    weighedForm: 'BaSO₄', weighedFormName: 'barium sulfate',
    weighedComposition: { Ba: 1, S: 1, O: 4 },
    analytePerWeighedForm: 1,
    ratioExplanation: 'Each BaSO₄ contains one S atom, so n(S) = n(BaSO₄). Use M(S), not M(SO₄²⁻), because the result is % S.',
    equations: ['Ba²⁺(aq) + SO₄²⁻(aq) → BaSO₄(s)'],
    treatment: IGNITE,
    sampleMassG: 0.5, emptyCrucibleG: 19.7731, modelPercent: 12.4,
    errorSources: [INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED],
  },
  {
    ...provenance,
    id: 'iron-supplement',
    title: 'Iron in iron supplement tablets',
    context: 'Powdered tablets (1.2000 g) are dissolved in acid and the iron is oxidised to Fe³⁺. Ammonia solution precipitates hydrated iron(III) oxide, which is ignited to Fe₂O₃.',
    analyte: 'Fe', analyteName: 'iron',
    analyteComposition: { Fe: 1 },
    weighedForm: 'Fe₂O₃', weighedFormName: 'iron(III) oxide',
    weighedComposition: { Fe: 2, O: 3 },
    analytePerWeighedForm: 2,
    ratioExplanation: 'Each Fe₂O₃ contains two Fe, so n(Fe) = 2 × n(Fe₂O₃).',
    equations: ['Fe³⁺(aq) + 3OH⁻(aq) → Fe(OH)₃(s)', '2Fe(OH)₃(s) → Fe₂O₃(s) + 3H₂O(g)  (ignition)'],
    treatment: IGNITE,
    sampleMassG: 1.2, emptyCrucibleG: 20.5148, modelPercent: 8.15,
    errorSources: [
      INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED,
      {
        id: 'incomplete-ignition',
        label: 'Hydrated oxide not fully converted to Fe₂O₃',
        effect: 'high',
        explanation: 'Leftover water of hydration is weighed as Fe₂O₃, so the iron result is too high.',
      },
    ],
  },
  {
    ...provenance,
    id: 'nickel-steel',
    title: 'Nickel in stainless steel',
    context: 'A 0.2000 g steel sample is dissolved in acid. Dimethylglyoxime (DMG) in a slightly alkaline solution precipitates red nickel dimethylglyoximate, Ni(C₄H₇N₂O₂)₂.',
    analyte: 'Ni', analyteName: 'nickel',
    analyteComposition: { Ni: 1 },
    weighedForm: 'Ni(C₄H₇N₂O₂)₂', weighedFormName: 'nickel dimethylglyoximate',
    weighedComposition: { Ni: 1, C: 8, H: 14, N: 4, O: 4 },
    analytePerWeighedForm: 1,
    ratioExplanation: 'Each Ni(C₄H₇N₂O₂)₂ contains one Ni, so n(Ni) = n(precipitate).',
    equations: ['Ni²⁺(aq) + 2C₄H₈N₂O₂(aq) → Ni(C₄H₇N₂O₂)₂(s) + 2H⁺(aq)'],
    treatment: DRY_IN_OVEN,
    sampleMassG: 0.2, emptyCrucibleG: 17.9264, modelPercent: 10.2,
    errorSources: [
      INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED,
      {
        id: 'excess-dmg',
        label: 'A large excess of DMG reagent crystallises with the precipitate',
        effect: 'high',
        explanation: 'DMG is only slightly soluble in water, so excess reagent can come out of solution and be weighed with the nickel complex.',
      },
    ],
  },
  {
    ...provenance,
    id: 'phosphorus-superphosphate',
    title: 'Phosphorus in superphosphate',
    context: 'Phosphate from 0.6000 g of superphosphate fertiliser is precipitated as MgNH₄PO₄·6H₂O, which is ignited to magnesium pyrophosphate, Mg₂P₂O₇.',
    analyte: 'P', analyteName: 'phosphorus (as P)',
    analyteComposition: { P: 1 },
    weighedForm: 'Mg₂P₂O₇', weighedFormName: 'magnesium pyrophosphate',
    weighedComposition: { Mg: 2, P: 2, O: 7 },
    analytePerWeighedForm: 2,
    ratioExplanation: 'Each Mg₂P₂O₇ contains two P, so n(P) = 2 × n(Mg₂P₂O₇).',
    equations: [
      'Mg²⁺(aq) + NH₄⁺(aq) + PO₄³⁻(aq) + 6H₂O(l) → MgNH₄PO₄·6H₂O(s)',
      '2MgNH₄PO₄·6H₂O(s) → Mg₂P₂O₇(s) + 2NH₃(g) + 13H₂O(g)  (ignition)',
    ],
    treatment: IGNITE,
    sampleMassG: 0.6, emptyCrucibleG: 22.3017, modelPercent: 8.8,
    errorSources: [INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED],
  },
  {
    ...provenance,
    id: 'magnesium-epsom',
    title: 'Magnesium in Epsom salt',
    context: 'A 1.5000 g sample of Epsom salt (MgSO₄·7H₂O) is dissolved. Magnesium is precipitated as MgNH₄PO₄·6H₂O and ignited to Mg₂P₂O₇.',
    analyte: 'Mg', analyteName: 'magnesium',
    analyteComposition: { Mg: 1 },
    weighedForm: 'Mg₂P₂O₇', weighedFormName: 'magnesium pyrophosphate',
    weighedComposition: { Mg: 2, P: 2, O: 7 },
    analytePerWeighedForm: 2,
    ratioExplanation: 'Each Mg₂P₂O₇ contains two Mg, so n(Mg) = 2 × n(Mg₂P₂O₇).',
    equations: [
      'Mg²⁺(aq) + NH₄⁺(aq) + PO₄³⁻(aq) + 6H₂O(l) → MgNH₄PO₄·6H₂O(s)',
      '2MgNH₄PO₄·6H₂O(s) → Mg₂P₂O₇(s) + 2NH₃(g) + 13H₂O(g)  (ignition)',
    ],
    treatment: IGNITE,
    sampleMassG: 1.5, emptyCrucibleG: 19.0482, modelPercent: 9.86,
    errorSources: [INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED],
  },
  {
    ...provenance,
    id: 'sulfate-epsom',
    title: 'Sulfate in Epsom salt',
    context: 'A 0.5000 g sample of Epsom salt is dissolved and acidified. Excess barium chloride precipitates the sulfate as barium sulfate.',
    analyte: 'SO₄²⁻', analyteName: 'sulfate',
    analyteComposition: { S: 1, O: 4 },
    weighedForm: 'BaSO₄', weighedFormName: 'barium sulfate',
    weighedComposition: { Ba: 1, S: 1, O: 4 },
    analytePerWeighedForm: 1,
    ratioExplanation: 'Each BaSO₄ contains one SO₄²⁻ ion, so n(SO₄²⁻) = n(BaSO₄).',
    equations: ['Ba²⁺(aq) + SO₄²⁻(aq) → BaSO₄(s)'],
    treatment: IGNITE,
    sampleMassG: 0.5, emptyCrucibleG: 20.9915, modelPercent: 38.97,
    errorSources: [INCOMPLETE_PRECIPITATION, COPRECIPITATION, WASHING_LOSS, NOT_DRIED],
  },
];

export function gravimetricProblemById(id: string): GravimetricProblem | null {
  return GRAVIMETRIC_PROBLEMS.find((problem) => problem.id === id) ?? null;
}

// ---- Deterministic model ----

export interface GravimetricModel {
  analyteMolarMass: number;
  weighedMolarMass: number;
  /** Mass of analyte per gram of weighed form. */
  gravimetricFactor: number;
  /** Dry precipitate mass at constant mass, g, to balance resolution. */
  precipitateMassG: number;
  /** Crucible + precipitate after each heating, g. */
  weighings: number[];
  /** Index of the weighing at which constant mass is first confirmed. */
  constantAtIndex: number;
  analyteMassG: number;
  percent: number;
}

export function gravimetricFactor(problem: GravimetricProblem): number {
  return (
    (problem.analytePerWeighedForm * molarMass(problem.analyteComposition)) /
    molarMass(problem.weighedComposition)
  );
}

export function buildGravimetricModel(problem: GravimetricProblem): GravimetricModel {
  const factor = gravimetricFactor(problem);
  const precipitateMassG = roundMass(
    (problem.sampleMassG * problem.modelPercent) / PERCENT / factor,
  );
  const weighings = RESIDUAL_WATER_PATTERN.map((fraction) =>
    roundMass(problem.emptyCrucibleG + precipitateMassG * (1 + fraction)),
  );
  const constantAtIndex = weighings.findIndex(
    (mass, index) => index > 0 && Math.abs(weighings[index - 1] - mass) <= CONSTANT_MASS_TOLERANCE_G,
  );
  const analyteMassG = precipitateMassG * factor;
  return {
    analyteMolarMass: molarMass(problem.analyteComposition),
    weighedMolarMass: molarMass(problem.weighedComposition),
    gravimetricFactor: factor,
    precipitateMassG,
    weighings,
    constantAtIndex,
    analyteMassG,
    percent: (analyteMassG / problem.sampleMassG) * PERCENT,
  };
}

/** Constant mass is confirmed once two successive weighings agree within the tolerance. */
export function isConstantMass(weighings: number[]): boolean {
  if (weighings.length < 2) return false;
  const last = weighings[weighings.length - 1];
  const previous = weighings[weighings.length - 2];
  return Math.abs(last - previous) <= CONSTANT_MASS_TOLERANCE_G;
}

export function formatMass(value: number): string {
  return value.toFixed(BALANCE_DECIMALS);
}

export function formatPercent(value: number): string {
  return value >= 10 ? value.toFixed(1) : value.toFixed(2);
}

// ---- Mole-ratio prediction ----

export const RATIO_OPTIONS: readonly number[] = [1, 2, 0.5];

export function ratioLabel(problem: GravimetricProblem, ratio: number): string {
  const factor = ratio === 1 ? '' : ratio === 0.5 ? '½ × ' : `${ratio} × `;
  return `n(${problem.analyte}) = ${factor}n(${problem.weighedForm})`;
}

export function checkRatioPrediction(problem: GravimetricProblem, ratio: number) {
  if (!RATIO_OPTIONS.includes(ratio)) {
    return { correct: false, feedback: 'That ratio is not one of the reviewed options.' };
  }
  if (ratio === problem.analytePerWeighedForm) {
    return { correct: true, feedback: `Correct. ${problem.ratioExplanation}` };
  }
  if (ratio === 0.5) {
    return {
      correct: false,
      feedback: `A formula unit cannot hold half an atom or ion. Count ${problem.analyte} in ${problem.weighedForm}: ${problem.ratioExplanation}`,
    };
  }
  return {
    correct: false,
    feedback: `Count the ${problem.analyte} units in the formula ${problem.weighedForm}. ${problem.ratioExplanation}`,
  };
}

// ---- Percentage answer ----

export type GravimetricMistake =
  | 'not-constant'
  | 'not-dried'
  | 'precipitate-as-analyte'
  | 'included-crucible'
  | 'wrong-ratio'
  | 'impossible'
  | 'unsupported'
  | 'other';

export interface GravimetricCheck {
  correct: boolean;
  mistake: GravimetricMistake | null;
  feedback: string;
}

function near(value: number, target: number): boolean {
  return Math.abs(value - target) <= Math.abs(target) * GRAVIMETRIC_RELATIVE_TOLERANCE;
}

export function gravimetricWorking(problem: GravimetricProblem, model: GravimetricModel): string {
  const finalGross = model.weighings[model.constantAtIndex];
  const nWeighed = model.precipitateMassG / model.weighedMolarMass;
  const nAnalyte = nWeighed * problem.analytePerWeighedForm;
  return [
    `m(${problem.weighedForm}) = ${formatMass(finalGross)} − ${formatMass(problem.emptyCrucibleG)} = ${formatMass(model.precipitateMassG)} g`,
    `n(${problem.weighedForm}) = ${formatMass(model.precipitateMassG)} ÷ ${model.weighedMolarMass.toFixed(2)} = ${nWeighed.toExponential(3)} mol`,
    `n(${problem.analyte}) = ${problem.analytePerWeighedForm} × ${nWeighed.toExponential(3)} = ${nAnalyte.toExponential(3)} mol`,
    `m(${problem.analyte}) = ${nAnalyte.toExponential(3)} × ${model.analyteMolarMass.toFixed(2)} = ${formatMass(model.analyteMassG)} g`,
    `% ${problem.analyte} = ${formatMass(model.analyteMassG)} ÷ ${formatMass(problem.sampleMassG)} × 100 = ${formatPercent(model.percent)}%`,
  ].join('; ');
}

/** Percentage that a weighing (crucible + precipitate) would give with the correct method. */
function percentFromGross(problem: GravimetricProblem, model: GravimetricModel, gross: number) {
  return (((gross - problem.emptyCrucibleG) * model.gravimetricFactor) / problem.sampleMassG) * PERCENT;
}

/**
 * Checks a learner percentage. `weighingsTaken` is how many heat-and-weigh cycles the learner
 * has done; an answer before constant mass is never accepted.
 */
export function checkGravimetricAnswer(
  problem: GravimetricProblem,
  weighingsTaken: number,
  answer: number,
): GravimetricCheck {
  const model = buildGravimetricModel(problem);
  if (!Number.isFinite(answer) || answer <= 0) {
    return { correct: false, mistake: 'unsupported', feedback: 'Enter a positive percentage by mass.' };
  }
  if (!Number.isInteger(weighingsTaken) || weighingsTaken < 1 || weighingsTaken > MAX_WEIGHINGS) {
    return { correct: false, mistake: 'unsupported', feedback: 'Heat and weigh the precipitate first.' };
  }
  const taken = model.weighings.slice(0, weighingsTaken);
  const working = gravimetricWorking(problem, model);

  if (!isConstantMass(taken)) {
    const latest = taken[taken.length - 1];
    const usedLatest = near(answer, percentFromGross(problem, model, latest));
    const gap =
      taken.length > 1
        ? `The last two weighings differ by ${formatMass(Math.abs(taken[taken.length - 2] - latest))} g, more than ${CONSTANT_MASS_TOLERANCE_G} g.`
        : 'One weighing cannot show that the mass has stopped changing.';
    return {
      correct: false,
      mistake: 'not-constant',
      feedback: `${usedLatest ? 'Your method is right, but the precipitate is not dry yet. ' : ''}${gap} Water is still leaving, so heat, cool and weigh again before calculating.`,
    };
  }

  if (near(answer, model.percent)) {
    return { correct: true, mistake: null, feedback: `Correct: ${working}.` };
  }
  const earlier = model.weighings.slice(0, model.constantAtIndex);
  if (earlier.some((gross) => near(answer, percentFromGross(problem, model, gross)))) {
    return {
      correct: false,
      mistake: 'not-dried',
      feedback: `You used an earlier weighing that still contained water. Use the constant mass, ${formatMass(model.weighings[model.constantAtIndex])} g. ${working}.`,
    };
  }
  if (near(answer, (model.precipitateMassG / problem.sampleMassG) * PERCENT)) {
    return {
      correct: false,
      mistake: 'precipitate-as-analyte',
      feedback: `That is the mass of ${problem.weighedForm} as a percentage of the sample. Only part of the precipitate is ${problem.analyte}: convert with moles and the ${problem.analytePerWeighedForm} : 1 ratio. ${working}.`,
    };
  }
  const grossPercent = ((model.weighings[model.constantAtIndex] * model.gravimetricFactor) / problem.sampleMassG) * PERCENT;
  if (near(answer, grossPercent) || near(answer, (model.weighings[model.constantAtIndex] / problem.sampleMassG) * PERCENT)) {
    return {
      correct: false,
      mistake: 'included-crucible',
      feedback: `Subtract the empty crucible (${formatMass(problem.emptyCrucibleG)} g) first. ${working}.`,
    };
  }
  const wrongRatios = RATIO_OPTIONS.filter((ratio) => ratio !== problem.analytePerWeighedForm);
  if (wrongRatios.some((ratio) => near(answer, (model.percent * ratio) / problem.analytePerWeighedForm))) {
    return {
      correct: false,
      mistake: 'wrong-ratio',
      feedback: `Check the mole ratio. ${problem.ratioExplanation} ${working}.`,
    };
  }
  if (answer > PERCENT) {
    return {
      correct: false,
      mistake: 'impossible',
      feedback: `A part cannot be more than 100% of the sample. ${working}.`,
    };
  }
  return { correct: false, mistake: 'other', feedback: `Follow the mass → moles → ratio → mass chain: ${working}.` };
}

// ---- Error-source prediction ----

export function checkErrorEffect(source: GravimetricErrorSource, predicted: ErrorEffect) {
  const correct = source.effect === predicted;
  const direction = source.effect === 'high' ? 'too high' : 'too low';
  return {
    correct,
    feedback: `${correct ? 'Correct' : `Not quite: the result is ${direction}`}. ${source.explanation}`,
  };
}
